import type { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

import { Button, Select, Text } from '@/_components';

import type { PaginationHandlerProps } from '../toolbar';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  pagination: PaginationHandlerProps;
  paginationPageState?: {
    per_page: number;
    current_page: number;
    total: number;
    to: number;
    from: number;
    last_page: number;
  };
  isNonSelectable?: boolean;
}

const options = [
  { name: '10', value: '10' },
  { name: '25', value: '25' },
  { name: '50', value: '50' },
  { name: '75', value: '75' },
  { name: '100', value: '100' },
  { name: '500', value: '500' },
];

export function DataTablePagination<TData>({
  table,
  pagination,
  paginationPageState,
  isNonSelectable = false,
}: DataTablePaginationProps<TData>) {
  let current_page = -1;
  let total = -1;
  let last_page = -1;
  if (paginationPageState) {
    current_page = paginationPageState.current_page;
    total = paginationPageState.total;
    last_page = paginationPageState.last_page;
  }
  const { per_page, handleNext, handlePrevious, handleSetLast, handleSetFirst, handleSetNumberOfPages } = pagination;

  return (
    <article className="flex flex-col gap-4 px-1 md:flex-row md:items-end md:justify-between">
      {!isNonSelectable && (
        <Text as="small" className="text-c-card-muted text-left font-medium whitespace-nowrap">
          {table.getFilteredSelectedRowModel().rows.length} of {table.getFilteredRowModel().rows.length} row(s)
          selected.
        </Text>
      )}

      <section className="xs:flex-row xs:items-end xs:justify-between flex w-full flex-col gap-4 md:justify-end">
        <div className="flex items-end gap-x-4 self-end">
          <Text as="small" className="text-c-card-muted font-medium whitespace-nowrap">
            Rows per page
          </Text>

          <Select
            className="w-16 max-w-16 min-w-16!"
            value={{ value: `${per_page}`, name: `${per_page}` }}
            getOptionLabel={(option) => option.name}
            isClearable={false}
            onChange={(e) => {
              handleSetNumberOfPages(Number(e?.value || options[2].value));
            }}
            menuPlacement="top"
            options={options}
          />
        </div>

        <section className="flex flex-nowrap items-center justify-end gap-x-2">
          <Text as="small" className="text-c-card-muted pr-2 font-medium whitespace-nowrap">
            Page {current_page} of {Math.ceil(total / per_page)}
          </Text>
          <Button
            as="outline"
            size="md"
            className="xs:hidden sm:flex"
            onClick={() => handleSetFirst(current_page)}
            disabled={current_page === 1}
            icon={ChevronsLeft}
            aria-label="First Page"
          />
          <Button
            as="outline"
            size="md"
            onClick={() => handlePrevious(current_page)}
            disabled={current_page === 1}
            icon={ChevronLeft}
            aria-label="Previous Page"
          />
          <Button
            as="outline"
            size="md"
            onClick={() => handleNext(current_page, last_page)}
            disabled={current_page === last_page}
            icon={ChevronRight}
            aria-label="Next Page"
          />
          <Button
            as="outline"
            size="md"
            className="xs:hidden sm:flex"
            onClick={() => handleSetLast(current_page, last_page)}
            disabled={current_page === last_page}
            icon={ChevronsRight}
            aria-label="Last Page"
          />
        </section>
      </section>
    </article>
  );
}
